import type { CustomerQuote, Quote } from '@talos/kyoko';
import { createNumericField } from './createNumericField';
import type { ModifyFixingEntry, ModifyFixingState } from './modifyFixingSlice';

export function quoteToModifyFixingState(quote: Quote): ModifyFixingState {
  const entries: ModifyFixingEntry[] = [];

  if (quote.FixingDetails?.MultilegFixingDetails) {
    const legsBySymbol = new Map(quote.legsRepresentative.map(leg => [leg.Symbol, leg]));

    for (const [symbol, fixingDetail] of Object.entries(quote.FixingDetails.MultilegFixingDetails)) {
      const leg = legsBySymbol.get(symbol);
      if (!leg) {
        continue;
      }

      entries.push({
        rowID: leg.Symbol,
        symbol: leg.Symbol,
        currency: leg.Currency,
        amountCurrency: leg.AmountCurrency,
        tradedQty: leg.TradedQty,
        tradedSide: leg.TradedSide,
        PricingReference: leg.PricingReference,
        PricingMode: quote.PricingMode,
        index: fixingDetail.Index,
        fixing: fixingDetail.Fixing,
        price: leg.TradedPx,
        amount: leg.TradedAmt,
        fixingField: createNumericField('Fixing', fixingDetail.Fixing),
        priceField: createNumericField('Price', leg.TradedPx),
        amountField: createNumericField('Amount', leg.TradedAmt),
      });
    }
  } else if (quote.FixingDetails) {
    entries.push({
      rowID: quote.Symbol,
      symbol: quote.Symbol,
      currency: quote.Currency,
      amountCurrency: quote.AmountCurrency,
      tradedQty: quote.TradedQty,
      tradedSide: quote.TradedSide,
      PricingReference: quote.PricingReference,
      PricingMode: quote.PricingMode,
      index: quote.FixingDetails.Index,
      fixing: quote.FixingDetails.Fixing,
      price: quote.TradedPx,
      amount: quote.TradedAmt,
      fixingField: createNumericField('Fixing', quote.FixingDetails.Fixing),
      priceField: createNumericField('Price', quote.TradedPx),
      amountField: createNumericField('Amount', quote.TradedAmt),
    });
  }

  return {
    entries,
    topLevelPriceField: createNumericField('Price', quote.TradedPx),
    topLevelAmountField: createNumericField('Amount', quote.TradedAmt),
    topLevelPrice: quote.TradedPx,
    topLevelAmount: quote.TradedAmt,
    topLevelPricingReference: quote.PricingReference,
  };
}

export function customerQuoteToModifyFixingState(customerQuote: CustomerQuote): ModifyFixingState {
  const entries: ModifyFixingEntry[] = [];

  if (customerQuote.FixingDetails?.MultilegFixingDetails && customerQuote.LegSummary) {
    const legSummaryBySymbol = new Map(customerQuote.LegSummary.map(leg => [leg.Symbol, leg]));

    for (const [symbol, fixingDetail] of Object.entries(customerQuote.FixingDetails.MultilegFixingDetails)) {
      const leg = legSummaryBySymbol.get(symbol);
      if (!leg) {
        continue;
      }

      entries.push({
        rowID: leg.Symbol,
        symbol: leg.Symbol,
        currency: leg.Currency,
        amountCurrency: leg.AmountCurrency,
        tradedQty: leg.TradedQty,
        tradedSide: leg.TradedSide,
        PricingReference: leg.PricingReference,
        PricingMode: customerQuote.PricingMode,
        index: fixingDetail.Index,
        fixing: fixingDetail.Fixing,
        price: leg.TradedPx,
        amount: leg.TradedAmt,
        fixingField: createNumericField('Fixing', fixingDetail.Fixing),
        priceField: createNumericField('Price', leg.TradedPx),
        amountField: createNumericField('Amount', leg.TradedAmt),
      });
    }
  } else if (customerQuote.FixingDetails && customerQuote.Symbol) {
    entries.push({
      rowID: customerQuote.Symbol,
      symbol: customerQuote.Symbol,
      currency: customerQuote.Currency,
      amountCurrency: customerQuote.AmountCurrency,
      tradedQty: customerQuote.TradedQty,
      tradedSide: customerQuote.tradedSideOrSide,
      PricingReference: customerQuote.PricingReference,
      PricingMode: customerQuote.PricingMode,
      index: customerQuote.FixingDetails.Index,
      fixing: customerQuote.FixingDetails.Fixing,
      price: customerQuote.TradedPx,
      amount: customerQuote.TradedAmt,
      fixingField: createNumericField('Fixing', customerQuote.FixingDetails.Fixing),
      priceField: createNumericField('Price', customerQuote.TradedPx),
      amountField: createNumericField('Amount', customerQuote.TradedAmt),
    });
  }

  return {
    entries,
    topLevelPriceField: createNumericField('Price', customerQuote.TradedPx),
    topLevelAmountField: createNumericField('Amount', customerQuote.TradedAmt),
    topLevelPrice: customerQuote.TradedPx,
    topLevelAmount: customerQuote.TradedAmt,
    topLevelPricingReference: customerQuote.PricingReference,
  };
}
